import styles from "./CreateNewCreditMemo.module.scss";
import classNames from "classnames/bind";
import { useEffect, useRef, useState } from "react";
import jobDetailsApi from "~/apis/jobDetails";
import { DragIcon, PlusIcon, XIcon } from "~/assets/icons";
import { PencilIcon } from "lucide-react";
import LoadingMainContent from "~/components/common/loading-main-content/LoadingMainContent";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import PrimaryInput from "~/components/common/primary-input/PrimaryInput";
import PrimarySelectInput from "~/components/common/primary-select-input/PrimarySelectInput";
import { useAppDispatch, useAppSelector } from "~/redux/hooks";
import {
  handleCreateCreditMemo,
  handleGetJobDetails,
  handleUpdateCreditMemo,
} from "~/thunks/job-details/jobDetailsThunk";
import { handleApiError, notify } from "~/utils/helper";
import { ICreditMemoJobDetails } from "~/utils/interface/jobDetails";

const cx = classNames.bind(styles);

interface CreateNewCreditMemoProps {
  jobId: string;
  onCancel: () => void;
  onSaveCreditMemo: () => void;
  listCreditMemo: ICreditMemoJobDetails[];
  idCreditMemo?: string;
  isEdit?: boolean;
}

interface LineItem {
  id?: string;
  itemNumber: string;
  itemName: string;
  description: string;
  quantity: number;
  uom: string;
  type: string;
  isDimensional: boolean;
  length: {
    value: number;
    uom: string;
  };
  markup: number;
  price: number;
}

interface Section {
  id: string;
  sectionTitle: string;
  sectionDescription: string;
  lines: LineItem[];
}

interface FormData {
  designTemplate: string;
  creditMemoDate: string;
  customerNotes: string;
  internalNotes: string;
  total: number;
  taxStatus: string;
  status: string;
}

interface FormErrors {
  [key: string]: string;
}

const CreateNewCreditMemo = ({
  jobId,
  onCancel,
  onSaveCreditMemo,
  listCreditMemo,
  idCreditMemo,
  isEdit = false,
}: CreateNewCreditMemoProps) => {
  const dispatch = useAppDispatch();
  const { jobDetails } = useAppSelector((state) => state.jobDetails);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    designTemplate: "",
    creditMemoDate: "",
    customerNotes: "",
    internalNotes: "",
    total: 0,
    taxStatus: "None",
    status: "",
  });

  const createEmptyLine = (): LineItem => ({
    id: `line-${Date.now()}`,
    itemNumber: "",
    itemName: "",
    description: "",
    quantity: 0,
    uom: "PCS",
    type: "Material",
    isDimensional: false,
    length: { value: 0, uom: "M" },
    markup: 0,
    price: 0,
  });

  const createEmptySection = (id?: string, title?: string): Section => ({
    id: id || `section-${Date.now()}`,
    sectionTitle: title || `Section 1`,
    sectionDescription: "",
    lines: [createEmptyLine()],
  });

  const [sections, setSections] = useState<Section[]>([
    createEmptySection("1", "Section 1"),
  ]);
  const [draggedSectionId, setDraggedSectionId] = useState<string | null>(null);
  const [draggedLine, setDraggedLine] = useState<{
    sectionId: string;
    index: number;
  } | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const [selectedSavedCreditMemo, setSelectedSavedCreditMemo] =
    useState<string>("none");
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [tempSectionTitle, setTempSectionTitle] = useState<string>("");
  const [sectionTitleErrors, setSectionTitleErrors] = useState<{
    [key: string]: string;
  }>({});
  const sectionTitleInputRefs = useRef<{
    [key: string]: HTMLDivElement | null;
  }>({});
  const sectionTitleInputElementRefs = useRef<{
    [key: string]: HTMLInputElement | null;
  }>({});

  useEffect(() => {
    dispatch(handleGetJobDetails(jobId));
  }, [dispatch, jobId]);

  // Handle click outside to save section title
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (editingSectionId) {
        const ref = sectionTitleInputRefs.current[editingSectionId];
        if (ref && !ref.contains(event.target as Node)) {
          const trimmedTitle = tempSectionTitle.trim();
          if (!trimmedTitle) {
            setSectionTitleErrors((prev) => ({
              ...prev,
              [editingSectionId]: "Section title is required",
            }));
            return;
          }
          setSections((prev) =>
            prev.map((s) =>
              s.id === editingSectionId
                ? { ...s, sectionTitle: trimmedTitle }
                : s
            )
          );
          setEditingSectionId(null);
          setTempSectionTitle("");
          setSectionTitleErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors[editingSectionId];
            return newErrors;
          });
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, [editingSectionId, tempSectionTitle]);

  // Auto-focus input when editing starts
  useEffect(() => {
    if (editingSectionId) {
      const inputElement =
        sectionTitleInputElementRefs.current[editingSectionId];
      if (inputElement) {
        setTimeout(() => {
          inputElement.focus();
          inputElement.select();
        }, 0);
      }
    }
  }, [editingSectionId]);

  useEffect(() => {
    const fetchCreditMemoDetails = async () => {
      try {
        if (idCreditMemo && isEdit) {
          setIsLoading(true);
          const response = await jobDetailsApi.getCreditMemoDetailsJobDetails(
            jobId,
            idCreditMemo
          );
          const creditMemoData = response.data?.data;

          if (creditMemoData) {
            setFormData({
              designTemplate: creditMemoData.designTemplate || "",
              creditMemoDate: creditMemoData.creditMemoDate || "",
              customerNotes: creditMemoData.customerNotes || "",
              internalNotes: creditMemoData.internalNotes || "",
              total: Number(creditMemoData.total) || 0,
              taxStatus: creditMemoData.taxStatus || "None",
              status: creditMemoData.status || "",
            });

            if (creditMemoData.sections && creditMemoData.sections.length > 0) {
              const baseTimestamp = Date.now();
              const mappedSections: Section[] = creditMemoData.sections.map(
                (section, sectionIndex) => ({
                  id: `section-${baseTimestamp}-${sectionIndex}`,
                  sectionTitle: section.sectionTitle?.trim() || `Section ${sectionIndex + 1}`,
                  sectionDescription: section.sectionDescription || "",
                  lines:
                    section.creditMemoSectionItems?.length > 0
                      ? section.creditMemoSectionItems.map((line, lineIndex) => ({
                        id: `line-${baseTimestamp}-${sectionIndex}-${lineIndex}`,
                        itemNumber: "",
                        itemName: line.name ?? line.itemName ?? "",
                        description: line.description || "",
                        quantity: line.quantity || 0,
                        uom: "PCS",
                        type: "Material",
                        isDimensional: false,
                        length: { value: 0, uom: "M" },
                        markup: line.markup || 0,
                        price: line.price || 0,
                      }))
                      : [createEmptyLine()],
                })
              );
              setSections(mappedSections);
            } else {
              setSections([
                {
                  id: "1",
                  sectionTitle: "Section 1",
                  sectionDescription: "",
                  lines: [
                    {
                      id: `line-${Date.now()}`,
                      itemNumber: "",
                      itemName: "",
                      description: "",
                      quantity: 0,
                      uom: "PCS",
                      type: "Material",
                      isDimensional: false,
                      length: { value: 0, uom: "M" },
                      markup: 0,
                      price: 0,
                    },
                  ],
                },
              ]);
            }
          }
          setIsLoading(false);
        }
      } catch (error: any) {
        console.error("Error fetching credit memo details:", error);
        handleApiError(error);
        setIsLoading(false);
      }
    };
    fetchCreditMemoDetails();
  }, [idCreditMemo, isEdit, jobId]);

  const getJobDisplayName = () => {
    const customerName =
      jobDetails?.relatedContacts?.primaryContact?.name ||
      jobDetails?.relatedContacts?.secondaryContact?.name ||
      "";
    const jobUniqueID = jobDetails?.jobId || "";

    if (customerName && jobUniqueID) {
      return `${customerName} - ${jobUniqueID}`;
    }
    if (customerName) {
      return customerName;
    }
    if (jobUniqueID) {
      return jobUniqueID;
    }
    return "";
  };

  const handleInputChange = (name: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDragStartSection = (
    e: React.DragEvent<HTMLDivElement>,
    sectionId: string
  ) => {
    setDraggedSectionId(sectionId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOverSection = (
    e: React.DragEvent<HTMLDivElement>,
    sectionId: string
  ) => {
    e.preventDefault();
    if (draggedSectionId === null || draggedSectionId === sectionId) return;
    const fromIndex = sections.findIndex((s) => s.id === draggedSectionId);
    const toIndex = sections.findIndex((s) => s.id === sectionId);
    if (fromIndex === -1 || toIndex === -1) return;
    const newSections = [...sections];
    const [removed] = newSections.splice(fromIndex, 1);
    newSections.splice(toIndex, 0, removed);
    setSections(newSections);
    setDraggedSectionId(sectionId);
  };

  const handleDragEndSection = () => {
    setDraggedSectionId(null);
  };

  const handleDragStartLine = (
    e: React.DragEvent<HTMLTableRowElement>,
    sectionId: string,
    index: number
  ) => {
    setDraggedLine({ sectionId, index });
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOverLine = (
    e: React.DragEvent<HTMLTableRowElement>,
    sectionId: string,
    index: number
  ) => {
    e.preventDefault();
    if (!draggedLine || draggedLine.sectionId !== sectionId) return;
    if (draggedLine.index === index) return;
    setSections((prev) => {
      const section = prev.find((s) => s.id === sectionId);
      if (!section) return prev;
      const newLines = [...section.lines];
      const [removed] = newLines.splice(draggedLine.index, 1);
      newLines.splice(index, 0, removed);
      return prev.map((s) =>
        s.id === sectionId ? { ...s, lines: newLines } : s
      );
    });
    setDraggedLine((prev) => (prev ? { ...prev, index } : null));
  };

  const handleDragEndLine = () => {
    setDraggedLine(null);
  };

  const updateLine = (
    sectionId: string,
    lineId: string,
    field: string,
    value: string | number | { value: number; uom: string }
  ) => {
    setSections((prev) =>
      prev.map((section) =>
        section.id === sectionId
          ? {
            ...section,
            lines: section.lines.map((line) =>
              line.id === lineId ? { ...line, [field]: value } : line
            ),
          }
          : section
      )
    );
  };

  const deleteLine = (sectionId: string, lineId: string) => {
    setSections((prev) =>
      prev.map((section) =>
        section.id === sectionId
          ? {
            ...section,
            lines: section.lines.filter((line) => line.id !== lineId),
          }
          : section
      )
    );
  };

  const deleteSection = (sectionId: string) => {
    if (sections.length <= 1) return;
    setSections(sections.filter((s) => s.id !== sectionId));
  };

  const updateSectionTitle = (sectionId: string, sectionTitle: string) => {
    setSections((prev) =>
      prev.map((s) => (s.id === sectionId ? { ...s, sectionTitle } : s))
    );
  };

  const handleStartEditSectionTitle = (sectionId: string) => {
    const section = sections.find((s) => s.id === sectionId);
    if (section) {
      setEditingSectionId(sectionId);
      setTempSectionTitle(section.sectionTitle);
      setSectionTitleErrors((prev) => ({ ...prev, [sectionId]: "" }));
    }
  };

  const calculateItemAmount = (qty: number, price: number) => {
    return (qty * price).toFixed(2);
  };

  const addItem = (sectionId: string) => {
    setSections((prev) =>
      prev.map((section) =>
        section.id === sectionId
          ? { ...section, lines: [...section.lines, createEmptyLine()] }
          : section
      )
    );
  };

  const handleAddSection = () => {
    setSections((prev) => [
      ...prev,
      createEmptySection(undefined, `Section ${prev.length + 1}`),
    ]);
  };

  const calculateGrossProfit = () => {
    return sections.reduce((acc, section) => {
      return (
        acc +
        section.lines.reduce(
          (sum, line) => sum + (line.quantity || 0) * (line.price || 0),
          0
        )
      );
    }, 0);
  };

  const getStructuredData = () => {
    return sections.map((s) => ({
      sectionTitle: s.sectionTitle,
      sectionDescription: s.sectionDescription,
      lines: s.lines.map((line) => ({
        itemNumber: line.id || "",
        itemName: line.itemName,
        description: line.description,
        quantity: line.quantity,
        uom: line.uom,
        type: line.type,
        isDimensional: line.isDimensional,
        length: line.length,
        markup: line.markup,
        price: line.price,
      })),
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.designTemplate.trim()) {
      newErrors.designTemplate = "Design template is required";
    }
    if (!formData.creditMemoDate) {
      newErrors.creditMemoDate = "Credit memo date is required";
    }

    const allLines = sections.flatMap((s) => s.lines);
    let hasLineItemError = false;
    const lineItemErrors: string[] = [];

    if (allLines.length === 0) {
      hasLineItemError = true;
      lineItemErrors.push("At least one line item is required");
    }

    allLines.forEach((line, index) => {
      if (!line.itemName || !line.itemName.trim()) {
        hasLineItemError = true;
        lineItemErrors.push(`Line item ${index + 1}: Item name is required`);
      }
    });

    if (hasLineItemError) {
      notify("error", lineItemErrors.join(". "));
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0 && !hasLineItemError;
  };

  const createPayload = (status: string) => {
    return {
      designTemplate: formData.designTemplate,
      taxStatus: formData.taxStatus || "None",
      creditMemoDate: formData.creditMemoDate,
      customerNotes: formData.customerNotes,
      internalNotes: formData.internalNotes,
      status: status,
      total: calculateGrossProfit(),
      sections: getStructuredData(),
    };
  };

  const updateCreditMemo = async (status: string) => {
    if (isSubmitting) return;
    if (!validateForm()) {
      return;
    }
    setIsSubmitting(true);
    const payload = createPayload(status);
    try {
      await dispatch(handleUpdateCreditMemo({ jobId, idCreditMemo: idCreditMemo!, data: payload })).unwrap();
      notify("success", "Credit memo updated successfully!");
      onSaveCreditMemo();
    } catch (error: any) {
      console.error("Failed to update credit memo:", error);
      notify(
        "error",
        error?.response?.data?.message || "Failed to update credit memo"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveAsDraft = async () => {
    if (isEdit && idCreditMemo) {
      await updateCreditMemo("DRAFT");
      return;
    }

    if (isSubmitting) return;
    if (!validateForm()) {
      return;
    }
    setIsSubmitting(true);
    const payload = createPayload("DRAFT");
    try {
      await dispatch(handleCreateCreditMemo({ jobId, data: payload })).unwrap();
      notify("success", "Credit memo created successfully!");
      onSaveCreditMemo();
    } catch (error: any) {
      console.error("Failed to create credit memo:", error);
      notify(
        "error",
        error?.response?.data?.message || "Failed to create credit memo"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveCreditMemo = async () => {
    if (isEdit && idCreditMemo) {
      await updateCreditMemo("OPEN");
      return;
    }

    if (isSubmitting) return;
    if (!validateForm()) {
      return;
    }
    setIsSubmitting(true);
    const payload = createPayload("OPEN");

    try {

      await dispatch(handleCreateCreditMemo({ jobId, data: payload })).unwrap();
      notify("success", "Credit memo created successfully!");
      onSaveCreditMemo();
    } catch (error: any) {
      console.error("Failed to create credit memo:", error);
      notify(
        "error",
        error?.response?.data?.message || "Failed to create credit memo"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSavedCreditMemoChange = async (value: string) => {
    setSelectedSavedCreditMemo(value);

    if (value === "none") {
      setFormData((prev) => ({
        // Keep current "Design Template" selection.
        ...prev,
        creditMemoDate: "",
        customerNotes: "",
        internalNotes: "",
        total: 0,
        taxStatus: "None",
        status: "",
      }));
      setSections([createEmptySection("1", "Section 1")]);
      return;
    }

    // Call API to get credit memo details
    try {
      setIsLoading(true);
      const response = await jobDetailsApi.getCreditMemoDetailsJobDetails(
        jobId,
        value
      );
      const creditMemoData = response.data?.data;

      if (creditMemoData) {
        // Map form data
        setFormData((prev) => ({
          ...prev,
          designTemplate: creditMemoData.designTemplate || "",
          creditMemoDate: creditMemoData.creditMemoDate || "",
          customerNotes: creditMemoData.customerNotes || "",
          internalNotes: creditMemoData.internalNotes || "",
          total: Number(creditMemoData.total) || 0,
          taxStatus: creditMemoData.taxStatus || "None",
          status: creditMemoData.status || "",
        }));

        // Map sections
        if (creditMemoData.sections && creditMemoData.sections.length > 0) {
          const baseTimestamp = Date.now();
          const mappedSections: Section[] = creditMemoData.sections.map(
            (section, sectionIndex) => ({
              id: `section-${baseTimestamp}-${sectionIndex}`,
              sectionTitle: section.sectionTitle?.trim() || `Section ${sectionIndex + 1}`,
              sectionDescription: section.sectionDescription || "",
              lines:
                section.creditMemoSectionItems?.length > 0
                  ? section.creditMemoSectionItems.map((line, lineIndex) => ({
                    id: `line-${baseTimestamp}-${sectionIndex}-${lineIndex}`,
                    itemNumber: "",
                    itemName: line.name ?? line.itemName ?? "",
                    description: line.description || "",
                        quantity: line.quantity || 0,
                        uom: "PCS",
                        type: "Material",
                        isDimensional: false,
                        length: { value: 0, uom: "M" },
                        markup: line.markup || 0,
                        price: line.price || 0,
                      }))
                      : [createEmptyLine()],
                })
              );
              setSections(mappedSections);
        } else {
          setSections([createEmptySection("1", "Section 1")]);
        }
      }
      setIsLoading(false);
    } catch (error) {
      handleApiError(error);
      setIsLoading(false);
    }
  };

  return (
    <div className={cx("create-new-credit-memo-container")}>
      <div className={cx("create-new-credit-memo-header")}>
        <h2 className={cx("title")}>
          {isEdit ? "Edit Credit Memo" : "Create New Credit Memo"}
        </h2>
      </div>

      {isLoading ? (
        <div className={cx("loading-overlay")}>
          <LoadingMainContent />
        </div>
      ) : (
        <div className={cx("create-new-credit-memo-body")}>
          <div className={cx("section")}>
            <div className={cx("info")}>
              <div className={cx("info-item")}>
                <div className={cx("row")}>
                  <label className={cx("label")}>Job</label>
                  <label className={cx("divider")}>:</label>
                  <p className={cx("value")}>{getJobDisplayName()}</p>
                </div>

                <div className={cx("row")}>
                  <label className={cx("label")}>Design Template</label>
                  <label className={cx("divider")}>:</label>
                  <PrimarySelectInput
                    name="designTemplate"
                    placeholder="Select Design Template"
                    value={formData.designTemplate}
                    onChange={(value) => {
                      handleInputChange("designTemplate", value);
                      if (errors.designTemplate) {
                        setErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.designTemplate;
                          return newErrors;
                        });
                      }
                    }}
                    options={[{ label: "Credit Memo", value: "Credit Memo" }]}
                    className={cx("select-input", {
                      error: !!errors.designTemplate,
                    })}
                    errorMessage={errors.designTemplate}
                  />
                </div>

                <div className={cx("row")}>
                  <label className={cx("label")}>Save Credit Memos</label>
                  <label className={cx("divider")}>:</label>
                  <PrimarySelectInput
                    name="saveCreditMemos"
                    placeholder="Select Save Credit Memos"
                    value={selectedSavedCreditMemo}
                    onChange={(value) => handleSavedCreditMemoChange(value)}
                    options={[
                      { label: "None", value: "none" },
                      ...listCreditMemo.map((creditMemo) => ({
                        label: creditMemo.creditMemoUniqueCode || "",
                        value: creditMemo.id,
                      })),
                    ]}
                    className={cx("select-input")}
                  />
                </div>
              </div>

              <div className={cx("info-item")}>
                <div className={cx("row")}>
                  <label className={cx("label")}>Contact</label>
                  <label className={cx("divider")}>:</label>
                  <p className={cx("value")}>
                    {jobDetails?.relatedContacts?.primaryContact?.name ||
                      jobDetails?.relatedContacts?.secondaryContact?.name ||
                      ""}
                  </p>
                </div>

                <div className={cx("row")}>
                  <label className={cx("label")}>Credit Memo Date</label>
                  <label className={cx("divider")}>:</label>
                  <PrimaryInput
                    name="creditMemoDate"
                    type="date"
                    placeholder="Select Credit Memo Date"
                    value={formData.creditMemoDate}
                    onChange={(e) => {
                      handleInputChange("creditMemoDate", e.target.value);
                      if (errors.creditMemoDate) {
                        setErrors((prev) => {
                          const newErrors = { ...prev };
                          delete newErrors.creditMemoDate;
                          return newErrors;
                        });
                      }
                    }}
                    className={cx("input", { error: !!errors.creditMemoDate })}
                    error={!!errors.creditMemoDate}
                    errorMessage={errors.creditMemoDate}
                  />
                </div>

                <div className={cx("row")}>
                  <label className={cx("label")}>Tax Status</label>
                  <label className={cx("divider")}>:</label>
                  <PrimarySelectInput
                    name="taxStatus"
                    placeholder="Select Tax Status"
                    value={formData.taxStatus || "None"}
                    onChange={(value) => handleInputChange("taxStatus", value)}
                    // Figma + BA (#48): only None / Applied for now; no tax amount is calculated yet.
                    options={[
                      { label: "None", value: "None" },
                      { label: "Applied", value: "Applied" },
                    ]}
                    className={cx("select-input")}
                  />
                </div>
              </div>
            </div>

            <div className={cx("sections-body")}>
              {sections.map((section) => (
                <div
                  key={section.id}
                  className={cx("section-block", {
                    "is-dragging": draggedSectionId === section.id,
                  })}
                  draggable
                  onDragStart={(e) => handleDragStartSection(e, section.id)}
                  onDragOver={(e) => handleDragOverSection(e, section.id)}
                  onDragEnd={handleDragEndSection}
                >
                  <button
                    type="button"
                    className={cx("drag-button")}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <DragIcon className={cx("drag-icon")} />
                  </button>
                  <div className={cx("section-table-container")}>
                    <div className={cx("section-table-header")}>
                      <div
                        className={cx("section-table-header-left")}
                        ref={(el) => {
                          sectionTitleInputRefs.current[section.id] = el;
                        }}
                      >
                        {editingSectionId === section.id ? (
                          <div className={cx("section-title-input-wrapper")}>
                            <input
                              ref={(el) => {
                                sectionTitleInputElementRefs.current[
                                  section.id
                                ] = el;
                              }}
                              name="section-title"
                              placeholder="Section Title"
                              value={tempSectionTitle}
                              onChange={(e) =>
                                setTempSectionTitle(e.target.value)
                              }
                              onKeyDown={(
                                e: React.KeyboardEvent<HTMLInputElement>
                              ) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  const trimmedTitle = tempSectionTitle.trim();
                                  if (!trimmedTitle) {
                                    setSectionTitleErrors((prev) => ({
                                      ...prev,
                                      [section.id]:
                                        "Section title is required",
                                    }));
                                    return;
                                  }
                                  updateSectionTitle(
                                    section.id,
                                    trimmedTitle
                                  );
                                  setEditingSectionId(null);
                                  setTempSectionTitle("");
                                  setSectionTitleErrors((prev) => {
                                    const newErrors = { ...prev };
                                    delete newErrors[section.id];
                                    return newErrors;
                                  });
                                } else if (e.key === "Escape") {
                                  setEditingSectionId(null);
                                  setTempSectionTitle("");
                                  setSectionTitleErrors((prev) => {
                                    const newErrors = { ...prev };
                                    delete newErrors[section.id];
                                    return newErrors;
                                  });
                                }
                              }}
                              className={cx(
                                "section-table-header-left-title",
                                "input",
                                {
                                  error: sectionTitleErrors[section.id],
                                }
                              )}
                            />
                            {sectionTitleErrors[section.id] && (
                              <p className={cx("error-message")}>
                                {sectionTitleErrors[section.id]}
                              </p>
                            )}
                          </div>
                        ) : (
                          <>
                            <p
                              className={cx(
                                "section-table-header-left-title"
                              )}
                            >
                              {section.sectionTitle}
                            </p>
                            <button
                              type="button"
                              className={cx(
                                "section-table-header-left-button"
                              )}
                              onClick={() =>
                                handleStartEditSectionTitle(section.id)
                              }
                            >
                              <PencilIcon className={cx("pencil-icon")} />
                            </button>
                          </>
                        )}
                      </div>

                      <div className={cx("section-table-header-right")}>
                        {sections.length > 1 && (
                          <button
                            type="button"
                            onClick={() => deleteSection(section.id)}
                            className={cx("section-header-delete-button")}
                          >
                            <XIcon className={cx("x-icon")} />
                          </button>
                        )}
                      </div>
                    </div>
                    <div
                      className={cx("table-wrapper")}
                      ref={section.id === sections[0]?.id ? tableWrapperRef : undefined}
                    >
                      <table className={cx("table")}>
                        <thead>
                          <tr className={cx("table-head-row")}>
                            <th className={cx("table-head-cell", "drag-cell")}></th>
                            <th className={cx("table-head-cell", "item-name")}>Item Name</th>
                            <th className={cx("table-head-cell", "description")}>Description</th>
                            <th className={cx("table-head-cell", "quantity")}>Quantity</th>
                            <th className={cx("table-head-cell", "ml")}>M/L</th>
                            <th className={cx("table-head-cell", "price")}>Price</th>
                            <th className={cx("table-head-cell", "amount")}>Amount</th>
                            <th className={cx("table-head-cell", "action")}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {section.lines.length > 0 ? (
                            section.lines.map((line, index) => (
                              <tr
                                key={line.id}
                                draggable
                                onDragStart={(e) =>
                                  handleDragStartLine(e, section.id, index)
                                }
                                onDragOver={(e) =>
                                  handleDragOverLine(e, section.id, index)
                                }
                                onDragEnd={handleDragEndLine}
                                className={cx("table-row", "item-row", {
                                  dragging:
                                    draggedLine?.sectionId === section.id &&
                                    draggedLine?.index === index,
                                })}
                              >
                                <td className={cx("table-cell", "drag-cell")}>
                                  <DragIcon className={cx("drag-icon")} />
                                </td>
                                <td className={cx("table-cell", "item-name")}>
                                  <PrimaryInput
                                    name="itemName"
                                    placeholder="Item Name"
                                    value={line.itemName}
                                    onChange={(e) =>
                                      updateLine(
                                        section.id,
                                        line.id!,
                                        "itemName",
                                        e.target.value
                                      )
                                    }
                                    className={cx("input")}
                                  />
                                </td>
                                <td className={cx("table-cell", "description")}>
                                  <PrimaryInput
                                    name="description"
                                    placeholder="Description"
                                    value={line.description}
                                    onChange={(e) =>
                                      updateLine(
                                        section.id,
                                        line.id!,
                                        "description",
                                        e.target.value
                                      )
                                    }
                                    className={cx("input")}
                                  />
                                </td>
                                <td className={cx("table-cell", "quantity")}>
                                  <PrimaryInput
                                    name="quantity"
                                    placeholder="Quantity"
                                    value={line.quantity?.toString() || ""}
                                    onChange={(e) =>
                                      updateLine(
                                        section.id,
                                        line.id!,
                                        "quantity",
                                        parseFloat(e.target.value) || 0
                                      )
                                    }
                                    className={cx("input")}
                                  />
                                </td>
                                <td className={cx("table-cell", "ml")}>
                                  <p className={cx("cell-value")}>
                                    {line.type === "Material" ? "M" : "L"}
                                  </p>
                                </td>
                                <td className={cx("table-cell", "price")}>
                                  <PrimaryInput
                                    name="price"
                                    placeholder="Price"
                                    value={line.price?.toString() || ""}
                                    onChange={(e) =>
                                      updateLine(
                                        section.id,
                                        line.id!,
                                        "price",
                                        parseFloat(e.target.value) || 0
                                      )
                                    }
                                    className={cx("input")}
                                  />
                                </td>
                                <td className={cx("table-cell", "amount")}>
                                  $
                                  {calculateItemAmount(
                                    line.quantity || 0,
                                    line.price || 0
                                  )}
                                </td>
                                <td className={cx("table-cell", "action")}>
                                  <div className={cx("action-cell-content")}>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        deleteLine(section.id, line.id!)
                                      }
                                      className={cx("delete-button")}
                                    >
                                      <XIcon className={cx("x-icon")} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr className={cx("table-row")}>
                              <td colSpan={8} className={cx("table-cell")}>
                                <p className={cx("no-data-text")}>No data</p>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    <div className={cx("section-block-add-item")}>
                      <PrimaryButton
                        title="Add item"
                        onClick={() => addItem(section.id)}
                        className={cx("add-item-button")}
                        icon={<PlusIcon className={cx("plus-icon")} />}
                        iconPosition="left"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className={cx("section-bottom")}>
              <div className={cx("section-bottom-left")}></div>
              <div className={cx("section-bottom-right")}>
                <PrimaryButton
                  title="Add Section"
                  onClick={handleAddSection}
                  className={cx("add-section-button")}
                  icon={<PlusIcon className={cx("plus-icon")} />}
                  iconPosition="left"
                />
              </div>
            </div>

            <div className={cx("table-footer")}>
              <div className={cx("total-section")}>
                <div className={cx("total-section-item")}>
                  <label className={cx("label", "sub-total-label")}>
                    Sub Total:
                  </label>
                  <p className={cx("value", "sub-total-value")}>${calculateGrossProfit().toFixed(2)}</p>
                </div>
                <div className={cx("total-section-item", "divider")}></div>
                <div className={cx("total-section-item")}>
                  <label className={cx("label")}>Gross Profit:</label>
                  <p className={cx("value")}>
                    ${calculateGrossProfit().toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className={cx("section", "!pt-0")}>
            <div className={cx("section-header")}>
              <h2 className={cx("title")}>Notes</h2>
            </div>

            <div className={cx("section-body")}>
              <div className={cx("section-item")}>
                <label className={cx("label")}>Customer Notes</label>
                <textarea
                  className={cx("textarea")}
                  placeholder="Enter notes"
                  value={formData.customerNotes}
                  onChange={(e) =>
                    handleInputChange("customerNotes", e.target.value)
                  }
                  rows={4}
                />
              </div>

              <div className={cx("section-item")}>
                <label className={cx("label")}>Internal Notes</label>
                <textarea
                  className={cx("textarea")}
                  placeholder="Enter notes"
                  value={formData.internalNotes}
                  onChange={(e) =>
                    handleInputChange("internalNotes", e.target.value)
                  }
                  rows={4}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={cx("create-new-credit-memo-footer")}>
        <PrimaryButton
          title="Cancel"
          onClick={onCancel}
          className={cx("cancel-button")}
          disabled={isSubmitting}
        />

        <PrimaryButton
          title="Save as Draft"
          onClick={handleSaveAsDraft}
          className={cx("save-draft-button")}
          disabled={isSubmitting}
        />

        <PrimaryButton
          title="Save credit memo"
          onClick={handleSaveCreditMemo}
          className={cx("save-button")}
          loading={isSubmitting}
          disabled={isSubmitting}
        />
      </div>
    </div>
  );
};

export default CreateNewCreditMemo;
